import {
  ChangeEventHandler,
  FormEventHandler,
  useEffect,
  useRef,
  useState,
} from "react";

import { useIsMutating } from "@enkor/react-query";

import BaseBottomSheet from "@domains/common/views/modal/BaseBottomSheet/BaseBottomSheet";
import { CloseIcon, MessageIcon } from "@enkor/design/icons";
import { grayScale, pink } from "@enkor/design/colors";
import useModalState from "@logics/hooks/useModalState";
import { eventMutationKeys } from "@domains/event/constants/eventMutationKeys";

import * as S from "./CommentForm.style";

interface CommentFormProps {
  payload?: string;
  headerTitle: string;
  onSubmitComment: (text: string) => void;
}

const CommentForm = ({
  payload,
  headerTitle,
  onSubmitComment,
}: CommentFormProps) => {
  const commentTextAreaRef = useRef<HTMLTextAreaElement>(null);

  const [commentPayload, setCommentPayload] = useState(payload || "");

  const { closeModal } = useModalState();
  const isCommentMutating = useIsMutating({
    mutationKey: eventMutationKeys.mutateEventComments(),
  });
  const isReplyMutating = useIsMutating({
    mutationKey: eventMutationKeys.mutateCommentReplies(),
  });

  const shouldDisableSend =
    !commentPayload || !!isCommentMutating || !!isReplyMutating;

  const handleResizeHeight = () => {
    if (!commentTextAreaRef?.current) return;

    const start = commentTextAreaRef.current.selectionStart;
    const end = commentTextAreaRef.current.selectionEnd;
    commentTextAreaRef.current.setSelectionRange(start, end); // NOTE: 커서 위치 유지

    commentTextAreaRef.current.style.height = "inherit";
    commentTextAreaRef.current.style.height = `${
      commentTextAreaRef.current.scrollHeight + 2
    }px`; // NOTE: 기존 Form height 크기와 동일하도록
  };

  const handleChangeTextArea: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setCommentPayload(e.target.value);
    handleResizeHeight();
  };

  const handleSubmitComment: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();

    if (!commentPayload) return;

    onSubmitComment(commentPayload);

    closeModal();
  };

  useEffect(() => {
    if (!commentTextAreaRef?.current) return;

    commentTextAreaRef.current.focus();
  }, []);

  return (
    <BaseBottomSheet setting={{ $withBackdropFilter: true, $noPadding: true }}>
      <S.Container>
        <S.CommentHeader>
          <S.HeaderTitle name="strongS">{headerTitle}</S.HeaderTitle>
          <button type="button" onClick={closeModal}>
            <CloseIcon size={20} strokeColor={grayScale.white} />
          </button>
        </S.CommentHeader>

        <S.CommentFormBox onSubmit={handleSubmitComment}>
          <S.CommentTextarea
            ref={commentTextAreaRef}
            value={commentPayload}
            placeholder="Type a message"
            rows={1}
            onChange={handleChangeTextArea}
          />
          <S.CommentSendButton type="submit" disabled={shouldDisableSend}>
            <MessageIcon
              strokeColor={commentPayload ? pink.main : grayScale.main}
            />
          </S.CommentSendButton>
        </S.CommentFormBox>

        {/* NOTE: 댓글 글자 수 제한 제거. 이후 필요할 시 사용 */}
        {/* {commentPayload.length >= MAX_COMMENT_LENGTH && (
          <S.InputErrorMessage name="s">
            Comments cannot exceed {MAX_COMMENT_LENGTH} characters.
          </S.InputErrorMessage>
        )} */}
      </S.Container>
    </BaseBottomSheet>
  );
};

export default CommentForm;
